import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Options for configuring the infinite scroll behavior.
 */
interface UseInfiniteScrollOptions {
  /**
   * Intersection threshold (0-1). Default: 0.1
   * The percentage of the sentinel element that must be visible to trigger.
   */
  threshold?: number;

  /**
   * Root margin for intersection observer. Default: '100px'
   * Useful for triggering before the sentinel is actually visible.
   */
  rootMargin?: string;
}

/**
 * Result from the useInfiniteScroll hook.
 */
interface UseInfiniteScrollResult {
  /**
   * Ref callback to attach to the sentinel element.
   * When this element comes into view, isIntersecting becomes true.
   */
  sentinelRef: (node: HTMLElement | null) => void;

  /**
   * Whether the sentinel element is currently in view.
   * Use this to trigger loading more data.
   */
  isIntersecting: boolean;
}

/**
 * Hook for implementing infinite scroll behavior using IntersectionObserver.
 *
 * Attach the sentinelRef to an element at the bottom of your list,
 * and use isIntersecting to know when to load more data.
 *
 * @example
 * ```tsx
 * function TimelineList() {
 *   const { sentinelRef, isIntersecting } = useInfiniteScroll();
 *   const { fetchNextPage, hasNextPage } = useTimelineViewState(...);
 *
 *   useEffect(() => {
 *     if (isIntersecting && hasNextPage) {
 *       fetchNextPage();
 *     }
 *   }, [isIntersecting, hasNextPage, fetchNextPage]);
 *
 *   return (
 *     <div>
 *       {events.map(event => <EventCard key={event.id} event={event} />)}
 *       <div ref={sentinelRef} /> {/* Sentinel element *\/}
 *     </div>
 *   );
 * }
 * ```
 */
export function useInfiniteScroll(
  options: UseInfiniteScrollOptions = {}
): UseInfiniteScrollResult {
  const { threshold = 0.1, rootMargin = '100px' } = options;
  const [isIntersecting, setIsIntersecting] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const sentinelRef = useCallback(
    (node: HTMLElement | null) => {
      // Cleanup previous observer
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      if (!node) {
        setIsIntersecting(false);
        return;
      }

      // Create new observer
      observerRef.current = new IntersectionObserver(
        ([entry]) => {
          setIsIntersecting(entry.isIntersecting);
        },
        { threshold, rootMargin }
      );

      observerRef.current.observe(node);
    },
    [threshold, rootMargin]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return { sentinelRef, isIntersecting };
}
